import React, { useState, useEffect } from "react";
import { Card, DatePicker, Select, Button, Row, Col, message, Spin, Table } from "antd";
import { PieChartOutlined } from "@ant-design/icons";
import moment from "moment";
import clientApi from "../../clients/store/api";
import adminApi from "../../orders/store/api";
import settingApi from "../../settings/store/api";
import driverApi from "../../drivers/store/api";
import AdminBreadCrumb from "components/AdminBreadCrumb";
import { buildFirstLevelFilterRoute } from "modules/shared/PickupDropoff/helper";
import "./RevenueAnalysis.scss";

const { RangePicker } = DatePicker;
const { Option } = Select;

const STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "Accepted", value: "accepted" },
  { label: "In Progress", value: "in-progress" },
  { label: "Completed", value: "completed" },
  { label: "Canceled", value: "canceled" },
  { label: "Failed", value: "failed" }
];

const REPORT_TYPE_OPTIONS = [
  { label: "By Service", value: "service" },
  { label: "By Vehicle Type", value: "vehicle" },
  { label: "By Client", value: "client" },
  { label: "By Driver", value: "driver" }
];

const RevenueAnalysis = () => {
  // Set default to last month (first day through last day)
  const getLastMonthRange = () => {
    const lastMonth = moment().subtract(1, "month");
    return [lastMonth.startOf("month").clone(), lastMonth.endOf("month").clone()];
  };

  const [dateRange, setDateRange] = useState(getLastMonthRange());
  const [selectedClient, setSelectedClient] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState(["completed"]);
  const [reportType, setReportType] = useState("service");
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [vehicleCategories, setVehicleCategories] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [loadingClients, setLoadingClients] = useState(true);

  const breadCrumbs = [
    { text: "BILLING", url: "/admin/billing-dashboard" },
    { text: "REPORTS", url: "/admin/billing/reports" },
    { text: "REVENUE ANALYSIS", url: "/admin/billing/reports/revenue-analysis" }
  ];

  // Load clients, services, vehicle categories, and drivers on mount
  useEffect(() => {
    loadClients();
    loadServices();
    loadVehicleCategories();
    loadDrivers();
  }, []);

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const response = await clientApi.getClientList({ pageSize: 10000 });
      const clientList = response.data || [];
      setClients(
        clientList.sort((a, b) => (a.clientName || "").localeCompare(b.clientName || ""))
      );
    } catch (error) {
      console.error("Error loading clients:", error);
      message.error("Failed to load clients");
    } finally {
      setLoadingClients(false);
    }
  };

  const loadServices = async () => {
    try {
      const response = await settingApi.getServiceList({ pageSize: 10000 });
      const serviceList = response.data || [];
      setServices(serviceList);
    } catch (error) {
      console.error("Error loading services:", error);
    }
  };

  const loadVehicleCategories = async () => {
    try {
      const response = await settingApi.getVehicleCategoryList({ pageSize: 10000 });
      const categoryList = response.data || [];
      setVehicleCategories(categoryList);
    } catch (error) {
      console.error("Error loading vehicle categories:", error);
    }
  };

  const loadDrivers = async () => {
    try {
      const response = await driverApi.getDriverList({ pageSize: 10000 });
      const driverList = response.data || [];
      setDrivers(driverList);
    } catch (error) {
      console.error("Error loading drivers:", error);
    }
  };

  const filterClients = (input, option) => {
    return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  };

  const handleRunReport = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.error("Please select a valid date range");
      return;
    }

    setLoading(true);
    try {
      // Build filter object matching RouteList pattern
      const rawFilter = {
        status: selectedStatus.length > 0 ? selectedStatus : [],
        pickupDateRange: null,
        closedDateRange: dateRange,
        startClosedDate: dateRange[0],
        endClosedDate: dateRange[1]
      };

      if (selectedClient !== "all") {
        rawFilter.clientId = selectedClient;
      }

      // Build the processed filter string array (same as RouteList)
      const processedFilter = buildFirstLevelFilterRoute(rawFilter);

      const params = {
        group: "flexio",
        page: 1,
        pageSize: 10000,
        filter: processedFilter,
        pickupFilter: rawFilter
      };

      const response = await adminApi.getRouteList(params);
      const routes = response.data || [];

      console.log("=== REVENUE ANALYSIS DEBUG ===");
      console.log("Total routes fetched:", routes.length);
      console.log("Report type:", reportType);
      if (routes.length > 0) {
        console.log("First route FULL object:", routes[0]);
        console.log("First route keys:", Object.keys(routes[0]));
      }

      // Build lookup maps
      const serviceMap = {};
      services.forEach((s) => {
        serviceMap[s.id] = s.name || s.serviceName || "Unknown Service";
      });
      console.log("Services loaded:", services.length);
      console.log("Service map:", serviceMap);

      const vehicleMap = {};
      vehicleCategories.forEach((v) => {
        vehicleMap[v.id] = v.name || v.categoryName || "Unknown Vehicle";
      });

      const clientMap = {};
      clients.forEach((c) => {
        clientMap[c.id] = c.clientName || c.name || "Unknown Client";
      });

      const driverMap = {};
      drivers.forEach((d) => {
        driverMap[d.id] =
          `${d.firstName || ""} ${d.lastName || ""}`.trim() || "Unknown Driver";
      });

      // Aggregate data based on report type
      const aggregated = {};

      routes.forEach((route, index) => {
        let key;
        let label;

        switch (reportType) {
          case "service":
            key = route.service?.id;
            label = route.service?.name || "Unknown Service";
            break;
          case "vehicle":
            key = route.vehicleCategory?.id;
            label = route.vehicleCategory?.name || "Unknown Vehicle Type";
            break;
          case "client":
            key = route.client?.id;
            label = route.client?.clientName || route.client?.name || "Unknown Client";
            break;
          case "driver":
            key = route.driver?.id;
            label =
              route.driver?.firstName && route.driver?.lastName
                ? `${route.driver.firstName} ${route.driver.lastName}`.trim()
                : "Unknown Driver";
            break;
          default:
            return;
        }

        if (index === 0) {
          console.log("First route processing:", {
            reportType,
            key,
            label,
            service: route.service,
            clientFinalPrice: route.clientFinalPrice
          });
        }

        if (!key) {
          console.log("Skipping route - no key found:", route.id);
          return;
        }

        if (!aggregated[key]) {
          aggregated[key] = {
            key,
            label,
            routeCount: 0,
            totalRevenue: 0
          };
        }

        const clientFinalPrice = parseFloat(route.clientFinalPrice) || 0;
        aggregated[key].routeCount += 1;
        aggregated[key].totalRevenue += clientFinalPrice;

        if (index === 0) {
          console.log("After processing first route, aggregated:", aggregated);
        }
      });

      const reportDataArray = Object.values(aggregated).sort(
        (a, b) => b.totalRevenue - a.totalRevenue
      );

      console.log("Aggregated data:", aggregated);
      console.log("Report data array:", reportDataArray);

      const totalRevenue = reportDataArray.reduce(
        (sum, item) => sum + item.totalRevenue,
        0
      );
      const totalRoutes = reportDataArray.reduce((sum, item) => sum + item.routeCount, 0);

      console.log("Total revenue:", totalRevenue);
      console.log("Total routes:", totalRoutes);

      setReportData({
        items: reportDataArray,
        totalRevenue,
        totalRoutes,
        dateRange: `${dateRange[0].format("MMM D, YYYY")} - ${dateRange[1].format(
          "MMM D, YYYY"
        )}`
      });
    } catch (error) {
      console.error("Error fetching report data:", error);
      message.error("Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const getReportTypeLabel = () => {
    const option = REPORT_TYPE_OPTIONS.find((opt) => opt.value === reportType);
    return option ? option.label : "Items";
  };

  const columns = [
    {
      title: getReportTypeLabel(),
      dataIndex: "label",
      key: "label",
      fixed: "left",
      width: 250
    },
    {
      title: "# Routes",
      dataIndex: "routeCount",
      key: "routeCount",
      align: "center",
      width: 120
    },
    {
      title: "Total Revenue",
      dataIndex: "totalRevenue",
      key: "totalRevenue",
      align: "right",
      width: 180,
      render: (val) => `$${val.toFixed(2)}`
    },
    {
      title: "% of Total",
      key: "percentage",
      align: "right",
      width: 120,
      render: (_, record) => {
        const percentage = reportData
          ? ((record.totalRevenue / reportData.totalRevenue) * 100).toFixed(1)
          : 0;
        return `${percentage}%`;
      }
    }
  ];

  return (
    <div className="revenue-analysis-page admin">
      <div className="common-header">
        <AdminBreadCrumb breadCrumbs={breadCrumbs} />
      </div>

      <div className="content-wrapper">
        <h2>Revenue Analysis Report</h2>
        <p style={{ marginBottom: 24, color: "#666" }}>
          Analyze revenue by service, vehicle type, client, or driver
        </p>

        <Card title="Report Parameters" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={6}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Date Range</div>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
              />
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Client</div>
              <Select
                showSearch
                value={selectedClient}
                onChange={setSelectedClient}
                filterOption={filterClients}
                style={{ width: "100%" }}
                loading={loadingClients}
                placeholder="Select client"
              >
                <Option value="all">All Clients</Option>
                {clients.map((client) => (
                  <Option key={client.id} value={client.id}>
                    {client.clientName}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Status</div>
              <Select
                mode="multiple"
                allowClear
                value={selectedStatus}
                onChange={setSelectedStatus}
                style={{ width: "100%" }}
                placeholder="Select status"
                options={STATUS_OPTIONS}
              />
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Report Type</div>
              <Select
                value={reportType}
                onChange={setReportType}
                style={{ width: "100%" }}
                options={REPORT_TYPE_OPTIONS}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Button
                type="primary"
                icon={<PieChartOutlined />}
                onClick={handleRunReport}
                loading={loading}
                style={{ width: "100%" }}
              >
                Run Report
              </Button>
            </Col>
          </Row>
        </Card>

        {reportData && (
          <Card
            title={`Revenue ${getReportTypeLabel()}`}
            extra={
              <div>
                <strong>Total Revenue: </strong>
                <span style={{ color: "#1890ff", fontSize: 18 }}>
                  ${reportData.totalRevenue.toFixed(2)}
                </span>
                <span style={{ marginLeft: 16 }}>
                  <strong>Total Routes: </strong>
                  {reportData.totalRoutes}
                </span>
              </div>
            }
          >
            <Table
              columns={columns}
              dataSource={reportData.items}
              rowKey="key"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) =>
                  `Total ${total} ${getReportTypeLabel().toLowerCase()}`
              }}
              scroll={{ x: 800 }}
            />
          </Card>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: "#666" }}>Generating report...</div>
          </div>
        )}

        {!reportData && !loading && (
          <Card>
            <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
              <PieChartOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <h3>Revenue Analysis Report</h3>
              <p>Select parameters and click Run Report to view the data.</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RevenueAnalysis;
